import { useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { getAppConfig } from '@/lib/utils';
import { FirebaseAuthUI } from '@/components/auth/FirebaseAuthUI';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useParams<{ locale: string }>();
  const [searchParams] = useSearchParams();
  const nextRoute = useMemo(() => searchParams.get('next') ?? '/', [searchParams]);

  const { user, loading, error, clearError } = useAuth();
  const appConfig = getAppConfig();

  useEffect(() => {
    if (!loading && user) {
      // Navigate to the next route, ensuring it includes the locale
      const fullPath = nextRoute.startsWith('/') ? `/${locale}${nextRoute}` : `/${locale}/${nextRoute}`;
      navigate(fullPath, { replace: true });
    }
  }, [loading, user, navigate, nextRoute, locale]);

  useEffect(() => {
    if (!loading && !user) {
      clearError();
    }
  }, [loading, user, clearError]);

  return (
    <div className="bg-background text-foreground flex min-h-svh items-center justify-center px-4 py-16">
      <div className="border-border/60 bg-card/70 w-full max-w-md space-y-8 rounded-3xl border p-8 shadow-2xl shadow-black/5 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3 text-center">
          {appConfig.logo && (
            <img
              src={appConfig.logo}
              alt={`${appConfig.companyName} logo`}
              className="h-12 w-auto dark:invert-0 invert"
            />
          )}
          <div className="space-y-1">
            <p className="text-muted-foreground font-mono text-xs tracking-[0.2em] uppercase">
              {t('auth.secureAccess')}
            </p>
            <h1 className="text-2xl font-semibold tracking-tight">{t('auth.signInToContinue')}</h1>
            <p className="text-muted-foreground text-sm">{t('auth.authenticateMessage')}</p>
          </div>
        </div>

        <div className="space-y-4">
          <FirebaseAuthUI nextRoute={nextRoute} />
        </div>

        {error && (
          <div className="text-destructive/90 border-destructive/40 bg-destructive/5 rounded-xl border px-4 py-3 text-sm">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
